import React, { ReactNode } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import KenColors from "./kenColors";

interface GlobalThemeProviderProps {
  children: ReactNode;
}

const theme = createTheme({
  palette: {
    KenColors: { ...KenColors },
    primary: {
      light: "#F0EDF6",
      main: "#604797",
      dark: "#604797",
      contrastText: "#fff",
    },
    secondary: {
      light: "#ff7961",
      main: "#f44336",
      dark: "#ba000d",
      contrastText: "#000",
    },
    error: {
      main: "#E75547",
    },
    success: {
      main: "#1BBE75",
    },
    card: {
      backgroundColor: "#F7F8FB",
      hover: "#EDF2FF",
    },
    labels: {
      main: "#061938",
      light: "#7A869A",
      medium: "#505F79",
    },
  },
  typography: {
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontFamily: ["Schibsted Grotesk"].join(","),
    body1: {
      fontSize: "16",
    },
    body2: {
      fontSize: 18,
      fontWeight: 500,
    },
    caption: {
      fontSize: "14px",
    },
    h1: {
      fontSize: "20px",
      fontWeight: 500,
      "@media (min-width:1710)": {
        fontSize: "15px",
      },
    },
    h2: {
      fontSize: "40px",
    },
    h3: {
      fontSize: "32px",
    },
    h4: {
      fontSize: "24px",
    },
    h5: {
      fontSize: "16px",
    },
    h6: {
      fontSize: "16px",
    },
    subtitle1: {
      fontSize: "14px",
      fontWeight: 400,
      color: "#FEFEFE",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          width: "100%",
          marginBottom: "16px",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: "#B5B3B3", // Text color
          backgroundColor: "#101010",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#B5B3B3",
        },
        focused: {
          color: "#B5B3B3",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E35C16",
            boxShadow: "0px 4px 30px 0px rgba(227, 92, 22, 0.30)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E35C16",
            boxShadow: "0px 4px 30px 0px rgba(227, 92, 22, 0.30)",
          },
        },
        notchedOutline: {
          borderColor: "#E35C16",
          boxShadow: "0px 4px 30px 0px rgba(227, 92, 22, 0.30)",
          borderRadius: "8px",
          borderWidth: ".5px",
        },
      },
    },

    MuiButton: {
      variants: [
        {
          props: { variant: "contained" },
          style: {
            textTransform: "capitalize",
            background: "#604797",
            color: "#fff",
            ":hover": {
              background: "#604797",
            },
          },
        },
        {
          props: { variant: "text" },
          style: {
            textTransform: "capitalize",
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            textTransform: "capitalize",
          },
        },
      ],
    },
  },
  breakpoints: {
    values: {
      xs: 0, // Extra small devices (phones)
      sm: 576, // Small devices (landscape phones)
      md: 768, // Medium devices (tablets)
      lg: 992, // Large devices (desktops)
      xl: 1200, // Extra large devices (large desktops)
      xxl: 1440, // Extra wide desktops (e.g., 1440px width monitors)
      HD: 1500,
      fullHD: 1710, // Full HD (1920px width) or larger screens like wide desktop monitors
      ultraHD: 1920, // 4k or larger monitors
    },
  },
});

const GlobalThemeProvider: React.FC<GlobalThemeProviderProps> = ({ children }) => {
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default GlobalThemeProvider;
